export function roundMoney(value) {
  return Math.round(Number(value || 0) * 100) / 100;
}

export function formatPeso(value) {
  const amount = new Intl.NumberFormat("en-PH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));
  return `PHP ${amount}`;
}

export function getRequiredAmountNow(grandTotal, paymentOption) {
  if (paymentOption === "full_payment") return roundMoney(grandTotal);
  if (paymentOption === "down_payment_50") return roundMoney(grandTotal * 0.5);
  return 0;
}

export function getVerifiedTotalPaid(booking = {}) {
  const paymentStatus = String(booking.paymentStatus || "").toLowerCase();
  const hasLegacyReceipt =
    Boolean(booking.receiptNumber || booking.receiptIssuedAt || booking.receiptUrl) ||
    (booking.receiptData && typeof booking.receiptData === "object" && Object.keys(booking.receiptData).length > 0);
  if (!["verified", "fully_paid", "downpayment_paid", "paid"].includes(paymentStatus) && !hasLegacyReceipt) {
    return 0;
  }
  return roundMoney(booking.amountPaid || 0);
}

export function buildReturnChargesSummary(booking = {}) {
  const assessment = booking.returnAssessment || {};
  const additionalPayment = booking.additionalPayment || {};
  const extensionInBookingTotal = (booking.extensionRequests || []).some(request => request.status === "approved" && request.pricing?.version === 2);
  const extensionFee = extensionInBookingTotal ? 0 : roundMoney(
    Math.max(Number(additionalPayment.extensionAmount || 0), Number(booking.extensionAdditionalAmount || 0))
  );
  const lateFee = roundMoney(
    Math.max(Number(assessment.additionalRentalAmount || 0), Number(additionalPayment.additionalRentalAmount || 0))
  );
  const damageFee = roundMoney(Number(assessment.damageFee || additionalPayment.damageFee || 0));
  const fuelAdjustment = roundMoney(Number(assessment.fuelAdjustment || additionalPayment.fuelAdjustment || 0));
  const cleaningFee = roundMoney(Number(assessment.cleaningFee || additionalPayment.cleaningFee || 0));
  const penaltyAmount = roundMoney(
    Math.max(Number(assessment.penaltyAmount || 0), Number(additionalPayment.penaltyAmount || 0))
  );
  const manualAdjustment = roundMoney(
    Math.max(
      Number(assessment.otherFee || 0),
      Number(additionalPayment.manualAdjustmentAmount || 0),
      Number(additionalPayment.otherFee || 0)
    )
  );
  const otherFee = roundMoney(penaltyAmount + manualAdjustment);
  const assessedTotal = assessment.assessedAt ? Number(assessment.totalAdditionalAmount || 0) : 0;
  const total = roundMoney(
    Math.max(assessedTotal, lateFee + damageFee + extensionFee + fuelAdjustment + cleaningFee + otherFee)
  );

  return { lateFee, damageFee, extensionFee, fuelAdjustment, cleaningFee, otherFee, total };
}

export function buildFinancialSummary(booking = {}) {
  const returnCharges = buildReturnChargesSummary(booking);
  const subtotal = roundMoney(
    booking.rentalSubtotalBeforeDiscount ||
      booking.baseRentalAmount ||
      booking.basePrice ||
      Number(booking.vehicleTotal || 0) + Number(booking.driverTotal || 0)
  );
  const deliveryFee = roundMoney(booking.deliveryFee || 0);
  const returnPickupFee = roundMoney(booking.returnPickupFee || 0);
  const driverFee = roundMoney(booking.driverTotal || 0);
  const addOnsTotal = roundMoney(booking.otherExtraCharges || booking.extraFee || 0);
  const discountAmount = roundMoney(booking.discountAmount || booking.discount || 0);
  const returnChargesTotal = returnCharges.total;
  const extensionInBookingTotal = (booking.extensionRequests || []).some(request => request.status === "approved" && request.pricing?.version === 2);
  const rawTotal = extensionInBookingTotal ? Number(booking.totalPrice || 0) : Number(booking.finalTotalAfterDiscount || booking.totalPrice || booking.finalTotal || booking.estimatedTotal || 0);
  const grandTotal = roundMoney(
    returnChargesTotal > 0 && booking.returnAssessment?.assessedAt
      ? Math.max(0, rawTotal - returnChargesTotal)
      : rawTotal
  );
  const requiredPaymentType =
    booking.paymentOption === "full_payment"
      ? "full_payment"
      : booking.paymentOption === "down_payment_50"
      ? "down_payment"
      : "";
  const computedRequiredAmount = getRequiredAmountNow(grandTotal, booking.paymentOption);
  const requiredAmountNow = roundMoney(computedRequiredAmount || booking.invoiceAmount || 0);
  const totalPaid = getVerifiedTotalPaid(booking);
  const remainingBookingBalance = roundMoney(Math.max(0, grandTotal - totalPaid));
  const verifiedFinalOrAdditionalPaid = roundMoney(
    Math.max(0, totalPaid - Math.min(totalPaid, grandTotal))
  );
  const finalBalance = roundMoney(
    Math.max(0, remainingBookingBalance + returnChargesTotal - verifiedFinalOrAdditionalPaid)
  );
  const settlementPaidTotal = roundMoney(
    booking.returnAssessment?.settlementStatus === "paid_to_admin" ||
    booking.returnAssessment?.settlementStatus === "paid_online_pending_verification"
      ? Math.max(0, totalPaid - grandTotal)
      : 0
  );

  return {
    subtotal,
    deliveryFee,
    returnPickupFee,
    driverFee,
    addOnsTotal,
    discountAmount,
    grandTotal,
    requiredPaymentType,
    requiredAmountNow,
    totalPaid,
    remainingBookingBalance,
    returnChargesTotal,
    settlementPaidTotal,
    finalBalance,
    isFullyPaid: remainingBookingBalance <= 0,
    isSettled: finalBalance <= 0,
    returnCharges,
  };
}
